import React, { createContext, useState, useContext, useCallback, useEffect } from 'react'
import { DataContext } from '../context/DataContext'
import { transformCart, transformCartTotals } from '../helpers/utils'
import { MagentoClientFactory } from '../classes/Magento/MagentoClientFactory'
import PropTypes from 'prop-types'
import { getCheckoutUrlBasedOnCompany } from '../helpers/magentoUtils'
import { useCmsAdapter } from '../hook/useCmsAdapter'
import { CompanyInstructionsFactory } from '../classes/Magento/CompanyInstructions/CompanyInstructionsFactory'

export const MagentoContext = createContext()

export const MagentoProvider = ({ children }) => {
  const {
    userInfo,
    addressInfo,
    billingInfo,
    setEcommerceInfo,
    setCartData,
    ecommerceInfo,
    setPaymentMethods,
    selectedShippingMethod,
    selectedServiceType,
    selectedPaymentMethod,
    companyInfo,
    discountInfo,
    setDiscountInfo,
    setUserInfo,
    setAddressInfo,
    isUserLoggedIn,
    setIsUserLoggedIn
  } = useContext(DataContext)

  const { getMagentoCouponFreeShipping } = useCmsAdapter()

  const [magentoClient, setMagentoClient] = useState(null)
  const [iframeMountedAcknowledge, setIframeMountedAcknowledge] = useState(false)

  const setLoggedUserData = useCallback((customerInfo) => {
    if (!customerInfo) return
    const { email, firstname, lastname, billingAddress, phoneNumber } = customerInfo
    const address = billingAddress.join(' ')
    setUserInfo(prevState => {
      return {
        ...prevState,
        email,
        names: `${firstname} ${lastname}`,
        phone: phoneNumber
      }
    })
    setAddressInfo(prevState => {
      return {
        ...prevState,
        userAddress: address,
        coords: null
      }
    })
  }, [setUserInfo, setAddressInfo])

  const setInitialInformationMagento = async (message, setInitialInfoReady) => {
    const { cms, quoteId, domain, currentUrl, userData, paymentMethods } = message
    if (cms !== 'magento') return

    updateEcommerceInfo({ cms, quoteId, domain, currentUrl })
    const client = createMagentoClient(userData, domain, quoteId, currentUrl)
    setMagentoClient(client)

    const cartTotals = await updateCartData(client, quoteId)
    setPaymentMethods(paymentMethods.paymentMethods)

    if (cartTotals?.coupon_code) {
      setDiscountInfo(prevState => ({
        ...prevState,
        discountCodes: [{ code: cartTotals.coupon_code, type: 'coupon' }]
      }))
    }

    setInitialInfoReady(true)
  }

  const updateEcommerceInfo = ({ cms, quoteId, domain, currentUrl }) => {
    setEcommerceInfo(prevState => ({ ...prevState, cms, magentoQuoteId: quoteId, domain, currentUrl }))
  }

  const createMagentoClient = (userData, domain, quoteId, currentUrl) => {
    const { isLogged, userToken: token, customer } = userData
    setLoggedUserData(isLogged ? customer : null)
    setIsUserLoggedIn(isLogged)
    return MagentoClientFactory.create(isLogged ? 'registered' : 'guest', domain, quoteId, currentUrl, token)
  }

  const resolveFreeShipping = async (couponCode) => {
    if (!couponCode) return false

    try {
      const { data } = await getMagentoCouponFreeShipping(couponCode)
      return Boolean(data?.isFreeShipping)
    } catch (error) {
      console.error('Error resolving Magento free shipping coupon', error)
      return false
    }
  }

  const isSameCoupon = (couponCode, otherCouponCode) => Boolean(couponCode) &&
    couponCode.toLowerCase() === otherCouponCode?.toLowerCase()

  const buildCartTotalsPatch = async (cartTotals, items, freeShippingRequest) => ({
    ...transformCartTotals(cartTotals, items),
    isFreeShipping: await freeShippingRequest
  })

  const updateCartData = async (client, quoteId) => {
    const [cart, cartTotals] = await Promise.all([client.getCart(), client.getCartTotals()])
    const cartTotalsPatch = await buildCartTotalsPatch(
      cartTotals,
      cart.items,
      resolveFreeShipping(cartTotals?.coupon_code)
    )
    setCartData(prevState => ({
      ...prevState,
      cartId: quoteId,
      lines: transformCart(cart.items),
      ...cartTotalsPatch
    }))
    return cartTotals
  }

  const acknowledgeIframeMounted = () => {
    setIframeMountedAcknowledge(true)
  }

  const isCheckoutEnabled = useCallback(() => {
    return companyInfo?.checkout > 0
  }, [companyInfo])

  const sendMountedPostMessage = useCallback(() => {
    if (!iframeMountedAcknowledge && companyInfo?.checkout) {
      window.parent.postMessage(
        {
          type: 'iframe-mounted-magento',
          origin: 'pinflag-checkout-iframe',
          message: isCheckoutEnabled()
        },
        '*'
      )
    }
  }, [iframeMountedAcknowledge, isCheckoutEnabled, companyInfo])

  useEffect(() => {
    const intervalId = setInterval(() => {
      sendMountedPostMessage()
      if (iframeMountedAcknowledge || (ecommerceInfo.cms && ecommerceInfo.cms !== 'magento')) {
        clearInterval(intervalId)
      }
    }, 1000)

    return () => clearInterval(intervalId)
  }, [iframeMountedAcknowledge, sendMountedPostMessage, ecommerceInfo])

  const removeIframeMagento = () => {
    window.parent.postMessage(
      {
        type: 'remove-iframe',
        origin: 'pinflag-checkout-iframe',
        message: true
      },
      '*'
    )
  }

  const redirectToHomeMagento = () => {
    window.parent.postMessage(
      {
        type: 'redirect-to-home',
        origin: 'pinflag-checkout-iframe',
        message: true
      },
      '*'
    )
  }

  const redirectToCheckoutMagento = () => {
    if (ecommerceInfo.cms !== 'magento') return
    const checkoutPath = getCheckoutUrlBasedOnCompany(companyInfo.companyName)
    window.parent.postMessage(
      {
        type: 'redirect-to-checkout',
        origin: 'pinflag-checkout-iframe',
        message: { checkoutPath }
      },
      '*'
    )
  }

  const setInformationMagento = useCallback(async () => {
    const params = createParams()
    const instructions = createInstructions(params)
    const checkoutPath = getCheckoutUrlBasedOnCompany(companyInfo.companyName)
    const streetNumber = addressInfo.userAddress.split(' ').slice(-1).join(' ')
    const streetName = addressInfo.userAddress.split(' ').slice(0, -1).join(' ')
    const streetAddress = [streetName, streetNumber, '']
    postMessageToParent(checkoutPath, instructions, streetAddress)
  }, [magentoClient, addressInfo, userInfo, ecommerceInfo, selectedShippingMethod, selectedServiceType, selectedPaymentMethod, companyInfo, discountInfo])

  const createParams = () => ({
    userInformation: userInfo,
    addressInformation: addressInfo,
    billingInformation: billingInfo,
    discountCodes: discountInfo.discountCodes,
    shippingMethod: selectedShippingMethod,
    serviceType: selectedServiceType,
    paymentMethod: selectedPaymentMethod,
    isUserLoggedIn
  })

  const createInstructions = (params) => {
    const needsBilling = params.billingInformation.corporateRut !== undefined && params.billingInformation.corporateRut !== ''
    const needsDifferentBillingAddress = params.billingInformation?.sameAddressAsShipping === false &&
      !!params.billingInformation?.address
    const instructionsFactory = new CompanyInstructionsFactory(params, companyInfo.companyName, isUserLoggedIn, needsBilling, needsDifferentBillingAddress)
    instructionsFactory.createInstructions()
    return instructionsFactory.getParsedInstructions()
  }

  const postMessageToParent = (checkoutPath, instructions, streetAddress) => {
    window.parent.postMessage({
      type: 'fill-form-and-place-order',
      origin: 'pinflag-checkout-iframe',
      message: { instructions, checkoutPath, streetAddress }
    }, '*')
  }

  const calculateMagentoShippingRates = useCallback(async (addressInfo) => {
    if (!magentoClient) return []
    try {
      const shippingMethodsMagento = await magentoClient.estimateShippingMethods(addressInfo, userInfo)
      return parseMagentoShippingMethods(shippingMethodsMagento)
    } catch (error) {
      return []
    }
  }, [magentoClient, addressInfo, userInfo])

  const parseMagentoShippingMethods = (shippingMethods) => {
    return shippingMethods.map(method => {
      return {
        courier: method.method_code.split(' - ')[0],
        description: method.method_title,
        name: method.carrier_title,
        packageDeliveryTime: method.method_title.split('Tiempo aproximado: ')[1] || method.method_title,
        price: method.amount,
        serviceType: method.method_code.split(' - ')[1] || method.method_code
      }
    })
  }

  const addDiscountCodeMagento = async (discountCode) => {
    await handleDiscountCodeChange(() => magentoClient.addDiscountCode(discountCode), discountCode)
  }

  const removeDiscountCodeMagento = async (discountCode) => {
    await handleDiscountCodeChange(() => magentoClient.removeDiscountCode(discountCode))
  }

  const handleDiscountCodeChange = async (codeAction, requestedCouponCode) => {
    let valid = false
    let errorMessage = ''

    const freeShippingRequest = resolveFreeShipping(requestedCouponCode)

    try {
      await codeAction()
      valid = true
    } catch (error) {
      valid = false
      errorMessage = error.response?.data?.message || 'An error occurred'
    } finally {
      const cartTotals = await magentoClient.getCartTotals()
      await updateCartAndDiscountInfo(cartTotals, valid, errorMessage, requestedCouponCode, freeShippingRequest)
    }
  }

  const updateCartAndDiscountInfo = async (cartTotals, valid, errorMessage, requestedCouponCode, freeShippingRequest) => {
    const cartTotalsPatch = await buildCartTotalsPatch(
      cartTotals,
      undefined,
      isSameCoupon(cartTotals.coupon_code, requestedCouponCode)
        ? freeShippingRequest
        : resolveFreeShipping(cartTotals.coupon_code)
    )

    setCartData(prevState => ({
      ...prevState,
      ...cartTotalsPatch,
      shippingCost: prevState.shippingCost
    }))

    setDiscountInfo(prevState => ({
      ...prevState,
      discountCodes: cartTotals.coupon_code ? [{ code: cartTotals.coupon_code, type: 'coupon' }] : [],
      discountError: valid ? undefined : { message: errorMessage }
    }))
  }

  const buildItemsDeliveryCostMagento = (cartData) => {
    if (!cartData) return undefined
    return cartData.lines.map(line => {
      return {
        variant_id: Number(line.variantId || line.id),
        sku: line.sku || line.id,
        quantity: line.quantity,
        name: line.productName,
        grams: line.grams,
        price: Number(line.unitPrice)
      }
    })
  }

  return (
    <MagentoContext.Provider value={{
      setInitialInformationMagento,
      removeIframeMagento,
      redirectToHomeMagento,
      setInformationMagento,
      calculateMagentoShippingRates,
      buildItemsDeliveryCostMagento,
      addDiscountCodeMagento,
      removeDiscountCodeMagento,
      acknowledgeIframeMounted,
      redirectToCheckoutMagento
    }}>
      {children}
    </MagentoContext.Provider>
  )
}

MagentoProvider.propTypes = {
  children: PropTypes.node.isRequired
}
